"use client";

import { useState, useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

const testimonials = [
{
  quote: "We raised $18,400 from our family in just 3 weeks. No awkward conversations, no loans. BabyBank made it feel like a celebration, not a burden.",
  name: "Priya & Raj Sharma",
  treatment: "IVF — 2nd Cycle",
  location: "Chicago, IL",
  raised: "$18,400",
  avatar: "https://images.unsplash.com/photo-1642698099201-7355466a6414",
  avatarAlt: "Priya and Raj Sharma, smiling couple",
  color: "#2D6A8F"
},
{
  quote: "The privacy was everything. We weren't ready to go public with our journey, but we needed help. BabyBank let us share only with people we trusted.",
  name: "Marcus & Jennifer Cole",
  treatment: "Egg Donation",
  location: "Atlanta, GA",
  raised: "$24,000",
  avatar: "https://images.unsplash.com/photo-1697649124905-5d16b03081ec",
  avatarAlt: "Marcus and Jennifer Cole, couple outdoors",
  color: "#5A8A6F"
},
{
  quote: "Our clinic actually recommended BabyBank. It was so easy to set up — and seeing our parents contribute meant the world. We felt so supported.",
  name: "Sofia & Lucas Reyes",
  treatment: "Surrogacy Journey",
  location: "Denver, CO",
  raised: "$31,500",
  avatar: "https://images.unsplash.com/photo-1637716095254-f4bf60e57cda",
  avatarAlt: "Sofia and Lucas Reyes, happy couple",
  color: "#E8A598"
}];


export default function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startAutoPlay = () => {
    intervalRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % testimonials.length);
    }, 5000);
  };

  useEffect(() => {
    startAutoPlay();
    return () => {if (intervalRef.current) clearInterval(intervalRef.current);};
  }, []);

  const handleSelect = (i: number) => {
    setActive(i);
    if (intervalRef.current) clearInterval(intervalRef.current);
    startAutoPlay();
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {entries.forEach((e) => {if (e.isIntersecting) e.target.classList.add("visible");});},
      { threshold: 0.15 }
    );
    sectionRef.current?.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const t = testimonials[active];

  return (
    <section className="py-24 md:py-32 relative overflow-hidden" ref={sectionRef}
    style={{ background: "linear-gradient(160deg, #FDF8F4 0%, #EBF4FA 60%, #FDF0ED 100%)" }}>
      {/* Decorative */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full"
        style={{ background: "radial-gradient(ellipse, rgba(45, 106, 143, 0.08) 0%, transparent 70%)" }} />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full"
        style={{ background: "radial-gradient(ellipse, rgba(232, 165, 152, 0.12) 0%, transparent 70%)" }} />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 space-y-4">
          <div className="reveal flex justify-center">
            <span className="section-tag-pink">Real Stories</span>
          </div>
          <h2 className="reveal font-heading font-bold text-ink" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", lineHeight: 1.1 }}>
            Families who made it happen
          </h2>
        </div>

        {/* Main testimonial */}
        <div className="reveal glass-card rounded-4xl p-8 md:p-14 text-center relative">
          {/* Quote mark */}
          <div className="absolute top-8 left-8 text-6xl font-heading leading-none select-none" style={{ color: t.color, opacity: 0.15 }} aria-hidden="true">"</div>

          {/* Avatar cluster */}
          <div className="flex justify-center mb-8">
            <div className="relative w-20 h-20">
              <AppImage
                src={t.avatar}
                alt={t.avatarAlt}
                width={80}
                height={80}
                className="rounded-full object-cover w-full h-full border-4 border-white shadow-warm-md" />
              
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-2 border-white flex items-center justify-center"
              style={{ background: t.color }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Quote */}
          <blockquote className="text-xl md:text-2xl font-heading font-medium text-ink leading-relaxed mb-8 max-w-3xl mx-auto" style={{ letterSpacing: "-0.01em" }}>
            "{t.quote}"
          </blockquote>

          {/* Attribution */}
          <div className="space-y-1 mb-6">
            <p className="font-heading font-bold text-ink">{t.name}</p>
            <p className="text-sm text-ink-muted">{t.treatment} · {t.location}</p>
          </div>

          {/* Raised badge */}
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm"
          style={{ background: `${t.color}15`, color: t.color, border: `1px solid ${t.color}25` }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" />
            </svg>
            Raised {t.raised} — debt-free
          </div>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-3 mt-8">
          {testimonials.map((_, i) =>
          <button
            key={i}
            onClick={() => handleSelect(i)}
            className="transition-all duration-300 rounded-full"
            style={{
              width: i === active ? "32px" : "8px",
              height: "8px",
              background: i === active ? t.color : "#EDE5DB"
            }}
            aria-label={`View testimonial ${i + 1}`} />

          )}
        </div>
      </div>
    </section>);

}